// Centralized Axios instance with CSRF handling & helpers
import axios from 'axios';

// Read a cookie value (e.g., 'csrftoken')
export function getCookie(name) {
  if (typeof document === 'undefined') return null;
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
}

export const api = axios.create({
  withCredentials: true,
});

let csrfEnsured = false;
export async function ensureCsrf() {
  if (csrfEnsured) return;
  try { await api.get('/api/csrf/'); } catch (_) { /* ignore */ }
  csrfEnsured = true;
}

// Auto attach CSRF token for unsafe methods
api.interceptors.request.use((config) => {
  const method = (config.method || '').toLowerCase();
  if (['post', 'put', 'patch', 'delete'].includes(method)) {
    const token = getCookie('csrftoken');
    if (token && !config.headers['X-CSRFToken']) {
      config.headers['X-CSRFToken'] = token;
    }
  }
  return config;
});

// Response interceptor for MFA timeout detection
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 403) {
        if (error.response.data?.mfa_required) {
          console.log('[API] MFA verification expired — emitting mfa_required event');
          try {
            window.dispatchEvent(new CustomEvent('mfa_required'));
          } catch (e) {
            window.location.href = '/mfa-verify';
          }
          return Promise.reject(error);
        }
      }
    return Promise.reject(error);
  }
);

// GLOBAL interceptor on the base axios object (affects all direct imports of axios)
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 403) {
      // Check if MFA timeout occurred
      if (error.response.data?.mfa_required) {
        console.log('[API] MFA verification expired — emitting mfa_required event');
        error.isMfaRequired = true;
        alert("You don't have permission to perform this action");
      }
    }
    return Promise.reject(error);
  }
);

export function patchFetchForMfa() {
  if (typeof window === 'undefined' || window.__MFA_FETCH_PATCHED) return;
  window.__MFA_FETCH_PATCHED = true;
  const originalFetch = window.fetch.bind(window);
  window.fetch = async (...args) => {
    const response = await originalFetch(...args);
    if (response.status === 403) {
      try {
        const data = await response.clone().json();
        if (data?.mfa_required) {
          console.log('[fetch] MFA verification expired — emitting mfa_required event');
          try {
            window.dispatchEvent(new CustomEvent('mfa_required'));
          } catch (e) {
            window.location.href = '/mfa-verify';
          }
        }
      } catch (_) {
        // ignore non-JSON or cloning errors
      }
    }
    return response;
  };
}

export default api;
